import type { unstable_RemoteThreadListAdapter as RemoteThreadListAdapter } from '@assistant-ui/react';
import { createAssistantStream } from 'assistant-stream';
import { HIDDEN_CONTEXT_MARKER, createThread, getThreadState, listThreads, deleteThread, updateThreadMetadata, markHasMetadataTitle } from '@/client/lib/chatApi';
import type { LangChainMessage } from '@assistant-ui/react-langgraph';
import { isBackgroundThreadMetadata } from '@/client/lib/backgroundThreads';

/**
 * Strip the hidden-context block (skill body, page context, etc.) from
 * a message's visible text before using it as a title. Without this, the
 * title falls back to the raw `/skill <!-- gaia-context --> ...` blob.
 */
function visibleText(content: string): string {
  const idx = content.indexOf(HIDDEN_CONTEXT_MARKER);
  return (idx >= 0 ? content.slice(0, idx) : content).trim();
}

export type ThreadListAdapterOptions = {
  /** Called after a new thread is created in initialize(). */
  onThreadCreated?: (threadId: string) => void;
  /** Called after a thread is deleted. */
  onThreadDeleted?: (threadId: string) => void;
  /** Returns metadata to attach when creating a new thread. */
  getMetadata?: () => Record<string, unknown> | undefined;
  /** When set, threads are filtered by this assistant_id (used to separate Cortex variants). */
  assistantId?: string;
};

function deriveTitle(
  metadata: Record<string, unknown> | undefined,
  stateValues: Record<string, unknown> | undefined,
  messages: LangChainMessage[],
): string {
  // 1. Legacy: conversation_title in thread metadata (old threads)
  const metaTitle = (metadata as { conversation_title?: string })?.conversation_title;
  if (metaTitle) return metaTitle;

  // 2. New: conversation_metadata in state values (new middleware)
  const cm = stateValues?.conversation_metadata as { title?: string } | undefined;
  if (cm?.title) return cm.title;

  // 3. Fallback: first human message content (strip hidden context first)
  const firstHuman = messages.find((m) => m.type === 'human');
  if (firstHuman && typeof firstHuman.content === 'string') {
    const visible = visibleText(firstHuman.content);
    if (visible) {
      return visible.length > 60 ? visible.slice(0, 60) + '...' : visible;
    }
  }

  return 'New Chat';
}

/**
 * Set of thread IDs that belong to the current user's personal history.
 * Populated by list() — used by the sidebar to hide non-personal threads
 * that assistant-ui adds to its internal state via initialize().
 */
export const personalThreadIds = new Set<string>();

export function createThreadListAdapter(
  opts: ThreadListAdapterOptions = {},
): RemoteThreadListAdapter {
  const knownThreadIds = new Set<string>();

  return {
    async list() {
      const results = (await listThreads('personal')).filter(
        (thread) => !isBackgroundThreadMetadata(thread.metadata),
      );
      const sorted = [...results]
        .sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime())
        .slice(0, 10);

      // Track personal thread IDs so the sidebar can hide non-personal threads
      personalThreadIds.clear();
      sorted.forEach((t) => personalThreadIds.add(t.thread_id));

      const threads = await Promise.all(
        sorted.map(async (thread) => {
          knownThreadIds.add(thread.thread_id);

          const metaTitle =
            (thread.metadata as { conversation_title?: string })?.conversation_title || '';

          // Fast path: metadata already has the title — no state loading needed
          if (metaTitle) {
            markHasMetadataTitle(thread.thread_id);
            return {
              remoteId: thread.thread_id,
              externalId: thread.thread_id,
              title: metaTitle,
              status: 'regular' as const,
            };
          }

          // Slow path: load state to derive title, then backfill metadata
          let title: string;
          try {
            const state = await getThreadState(thread.thread_id);
            const values = state.values as Record<string, unknown> | undefined;
            const messages = (values?.messages as LangChainMessage[]) ?? [];
            title = deriveTitle(thread.metadata as Record<string, unknown>, values, messages);

            // Fire-and-forget: backfill metadata so future list() calls skip state loading
            if (title && title !== 'New Chat') {
              markHasMetadataTitle(thread.thread_id);
              void updateThreadMetadata(thread.thread_id, { conversation_title: title }).catch(() => {});
            }
          } catch {
            title = 'New Chat';
          }

          return {
            remoteId: thread.thread_id,
            externalId: thread.thread_id,
            title,
            status: 'regular' as const,
          };
        }),
      );

      return { threads };
    },

    // assistant-ui only invokes adapter.initialize when the active thread is
    // its `__LOCALID_*` placeholder; deep-linked threads go through fetch().
    // So this is always a "create a new thread" call — no existence probe needed.
    async initialize() {
      const meta = opts.getMetadata?.();
      const fullMeta = opts.assistantId
        ? { ...meta, assistant_id: opts.assistantId }
        : meta;
      const { thread_id } = await createThread(fullMeta);
      knownThreadIds.add(thread_id);
      personalThreadIds.add(thread_id);

      opts.onThreadCreated?.(thread_id);

      return { remoteId: thread_id, externalId: thread_id };
    },

    async rename(remoteId: string, newTitle: string) {
      await updateThreadMetadata(remoteId, { conversation_title: newTitle });
      markHasMetadataTitle(remoteId);
    },

    async archive(remoteId: string) {
      // No native archive in LangGraph — treat as delete
      await deleteThread(remoteId);
      knownThreadIds.delete(remoteId);
      opts.onThreadDeleted?.(remoteId);
    },

    async unarchive(_remoteId: string) {
      // No-op — not supported
    },

    async delete(remoteId: string) {
      await deleteThread(remoteId);
      knownThreadIds.delete(remoteId);
      opts.onThreadDeleted?.(remoteId);
    },

    async fetch(threadId: string) {
      return {
        remoteId: threadId,
        externalId: threadId,
        status: 'regular' as const,
      };
    },

    async generateTitle(remoteId, messages) {
      let title = '';

      // Try to get the conversation title — prefer state (most up-to-date from middleware)
      try {
        const state = await getThreadState(remoteId);
        const values = state.values as Record<string, unknown> | undefined;
        const cm = values?.conversation_metadata as { title?: string } | undefined;
        if (cm?.title) {
          title = cm.title;
          // Fire-and-forget: mirror to metadata for future list() calls
          markHasMetadataTitle(remoteId);
          void updateThreadMetadata(remoteId, { conversation_title: title }).catch(() => {});
        }
      } catch {
        // ignore — fall through to message-based title
      }

      if (!title) {
        const firstHuman = messages.find((m) => m.role === 'user');
        const rawContent =
          typeof firstHuman?.content === 'string'
            ? firstHuman.content
            : Array.isArray(firstHuman?.content)
              ? firstHuman.content
                  .filter(
                    (p): p is { type: 'text'; text: string } =>
                      (p as { type: string }).type === 'text',
                  )
                  .map((p) => p.text)
                  .join(' ')
              : '';

        const content = visibleText(rawContent) || 'New Chat';
        title = content.length > 60 ? content.slice(0, 60) + '...' : content;
      }

      return createAssistantStream((controller) => {
        controller.appendText(title);
        controller.close();
      });
    },
  };
}
